import * as Sentry from "@sentry/react";
import { wasPreloadErrorHandled } from "./app-version-error-deduplication";
import {
  getAppErrorData,
  isExpectedAppError,
  isExpectedUnauthenticatedError,
} from "./convex-errors";

const GENERIC_CONVEX_CLIENT_MESSAGE = "Your request couldn't be completed. Try again later.";
const NO_AVAILABLE_WORKERS_MESSAGE = "There are no available workers to process the request";
const TOO_MANY_CONCURRENT_REQUESTS_MESSAGE =
  "Too many concurrent requests in a short period of time";

const CONVEX_FUNCTION_TAG_KEYS = [
  "func",
  "function",
  "function_path",
  "functionPath",
  "convex.function",
  "convex_function",
] as const;

type ConvexSentryErrorClass =
  | "generic_redacted"
  | "no_available_workers"
  | "too_many_concurrent_requests";

type ConvexSentryGroupingScope = "client" | "platform";

export type ConvexSentryClassification = {
  errorClass: ConvexSentryErrorClass;
  functionPath?: string;
  groupingScope: ConvexSentryGroupingScope;
  fingerprint: string[];
};

export function shouldDropSentryEvent(error: unknown): boolean {
  return isExpectedUnauthenticatedError(error) || isExpectedAppError(error);
}

/**
 * Tag and fingerprint captured coded app errors by their code + Convex function,
 * so distinct server failures of one operation stop collapsing into a single
 * issue behind the redacted "Server Error" client message.
 */
export function applyAppErrorSentryGrouping<TEvent extends Sentry.Event>(
  event: TEvent,
  hint?: Sentry.EventHint
): TEvent | null {
  const appError = getAppErrorData(hint?.originalException);
  if (!appError) return null;

  event.tags = { ...event.tags, app_error_code: appError.code };
  event.fingerprint = [
    "convex-app-error",
    appError.code,
    getConvexFunctionPathFromSentryEvent(event, hint) ?? "unknown_function",
  ];
  return event;
}

function normalizeSearchText(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function getUnknownMessage(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;
  if (value && typeof value === "object" && "message" in value) {
    const message = (value as { message?: unknown }).message;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

function getSentryTag(event: Sentry.Event, key: string): string | undefined {
  const value = event.tags?.[key];
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

function getSentryContextValue(
  event: Sentry.Event,
  contextKey: string,
  valueKey: string
): string | undefined {
  const context = event.contexts?.[contextKey];
  if (!context || typeof context !== "object") return undefined;

  const value = (context as Record<string, unknown>)[valueKey];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function collectSentryText(event: Sentry.Event, hint?: Sentry.EventHint): string {
  const values: string[] = [];

  if (event.message) values.push(event.message);

  for (const exception of event.exception?.values ?? []) {
    if (exception.type) values.push(exception.type);
    if (exception.value) values.push(exception.value);
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.message) values.push(breadcrumb.message);
  }

  const originalExceptionMessage = getUnknownMessage(hint?.originalException);
  if (originalExceptionMessage) values.push(originalExceptionMessage);

  return values.join("\n");
}

function normalizeConvexFunctionPath(value: string | undefined): string | undefined {
  if (!value) return undefined;

  const trimmed = value.trim();
  const withoutKind =
    /^(query|mutation|action|internalQuery|internalMutation|internalAction):(.+)$/i.exec(
      trimmed
    )?.[2] ?? trimmed;

  if (withoutKind.length > 160) return undefined;
  if (!withoutKind.includes(":")) return undefined;
  if (!/^[A-Za-z0-9_./:-]+$/.test(withoutKind)) return undefined;

  return withoutKind;
}

function findConvexFunctionPathInText(text: string): string | undefined {
  const convexFunctionMatch = /\[CONVEX\s+[A-Z]+\(([^)]+)\)\]/.exec(text);
  return normalizeConvexFunctionPath(convexFunctionMatch?.[1]);
}

export function getConvexFunctionPathFromSentryEvent(
  event: Sentry.Event,
  hint?: Sentry.EventHint
): string | undefined {
  for (const key of CONVEX_FUNCTION_TAG_KEYS) {
    const tagValue = normalizeConvexFunctionPath(getSentryTag(event, key));
    if (tagValue) return tagValue;
  }

  for (const valueKey of ["function_path", "functionPath", "function", "func"]) {
    const contextValue = normalizeConvexFunctionPath(
      getSentryContextValue(event, "convex", valueKey)
    );
    if (contextValue) return contextValue;
  }

  return findConvexFunctionPathInText(collectSentryText(event, hint));
}

export function classifyGenericConvexSentryEvent(
  event: Sentry.Event,
  hint?: Sentry.EventHint
): ConvexSentryClassification | null {
  const text = collectSentryText(event, hint);
  const normalizedText = normalizeSearchText(text);

  let errorClass: ConvexSentryErrorClass | null = null;
  let groupingScope: ConvexSentryGroupingScope = "client";

  if (normalizedText.includes(normalizeSearchText(NO_AVAILABLE_WORKERS_MESSAGE))) {
    errorClass = "no_available_workers";
    groupingScope = "platform";
  } else if (normalizedText.includes(normalizeSearchText(TOO_MANY_CONCURRENT_REQUESTS_MESSAGE))) {
    errorClass = "too_many_concurrent_requests";
    groupingScope = "platform";
  } else if (normalizedText.includes(normalizeSearchText(GENERIC_CONVEX_CLIENT_MESSAGE))) {
    errorClass = "generic_redacted";
  }

  if (!errorClass) return null;

  const functionPath = getConvexFunctionPathFromSentryEvent(event, hint);
  const fingerprint =
    groupingScope === "platform"
      ? ["convex-platform", errorClass]
      : ["convex-client", errorClass, functionPath ?? "unknown_function"];

  return {
    errorClass,
    functionPath,
    groupingScope,
    fingerprint,
  };
}

export function applyGenericConvexSentryFingerprint<TEvent extends Sentry.Event>(
  event: TEvent,
  hint?: Sentry.EventHint
): TEvent {
  const classification = classifyGenericConvexSentryEvent(event, hint);
  if (!classification) return event;

  event.tags = {
    ...event.tags,
    convex_error_class: classification.errorClass,
    convex_grouping_scope: classification.groupingScope,
    ...(classification.functionPath ? { convex_function: classification.functionPath } : {}),
  };
  event.fingerprint = classification.fingerprint;

  return event;
}

type SentryEnvironment = "production" | "preview" | "uat" | "demo" | "development";

function isSentryEnvironment(value: unknown): value is SentryEnvironment {
  return (
    value === "production" ||
    value === "preview" ||
    value === "uat" ||
    value === "demo" ||
    value === "development"
  );
}

function resolveEnvironment(): SentryEnvironment {
  const dfEnvironment = import.meta.env.VITE_DF_ENVIRONMENT;
  if (isSentryEnvironment(dfEnvironment)) return dfEnvironment;

  const vercelEnv = import.meta.env.VITE_VERCEL_ENV;
  if (vercelEnv === "production" || vercelEnv === "preview") return vercelEnv;
  if (isSentryEnvironment(import.meta.env.MODE)) {
    return import.meta.env.MODE;
  }
  return "development";
}

export function initSentry() {
  if (import.meta.env.DEV) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const environment = resolveEnvironment();
  // Application-owned tags shared with the Convex backend, whose Sentry events come from
  // Convex's managed log stream (no SDK of ours): `df_environment` (LAT-841) and `df_release`
  // (LAT-911, the full commit SHA — `RELEASE` on Convex, `VITE_BUILD_ID` here; vite.config.ts
  // defines it as `local` for builds without a SHA). Sentry's native `release` stays
  // `web-app@<sha7>` for release health; `df_release:<sha>` is the one search that matches
  // browser and backend events of the same deploy.
  const buildRelease = import.meta.env.VITE_BUILD_ID || "unknown";

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_RELEASE,

    // Filter out expected application errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Missing identity is expected auth state, and coded app errors the UI
      // handles as normal outcomes (invalid input, conflicts, stale versions)
      // are expected failures. Other auth errors, such as INVALID_AUTH, can
      // indicate JWT configuration problems and stay visible, as do app-error
      // codes that signal bugs or infrastructure trouble.
      if (shouldDropSentryEvent(error)) {
        return null;
      }

      if (wasPreloadErrorHandled(error) && event.tags?.feature !== "app-version-recovery") {
        return null;
      }

      return (
        applyAppErrorSentryGrouping(event, hint) ?? applyGenericConvexSentryFingerprint(event, hint)
      );
    },
  });

  Sentry.setTag("df_environment", environment);
  Sentry.setTag("df_release", buildRelease);
}

export function setSentryIdentity(input: { userId?: string; orgId?: string }) {
  Sentry.setUser(input.userId ? { id: input.userId } : null);
  Sentry.setTag("orgId", input.orgId ?? "unknown");
}

export function setSentryAppContext(input: { routeFamily?: string; workspaceKind?: string }) {
  Sentry.setTag("routeFamily", input.routeFamily ?? "unknown");
  Sentry.setTag("workspaceKind", input.workspaceKind ?? "none");
  Sentry.setContext("app", {
    routeFamily: input.routeFamily ?? "unknown",
    workspaceKind: input.workspaceKind ?? "none",
  });
}
